import { Injectable } from '@angular/core';
import { ModalController, NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { confirmPasswordReset, getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';

import { ContractsModalComponent } from '@components/contract-signing/contracts-modal/contracts-modal.component';
import { environment } from '@environments/environment';
import { DpStorageService, DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import { initializeApp } from 'firebase/app';
import { AnalyticsService } from './analytics.service';
import { RestService } from './rest.service';

initializeApp(environment.firebaseConfig);

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private analytics: AnalyticsService,
    private utils: DpUtilsService,
    private translate: TranslateService,
    private nav: NavController,
    private rest: RestService,
    private storage: DpStorageService,
    private modalController: ModalController
  ) {
  }

  getCurrentUser() {
    const auth = getAuth();
    return auth.currentUser;
  }

  async getToken() {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      return await currentUser.getIdToken();
    } else {
      return null;
    }
  }

  async confirmPasswordReset(oobCode: string, password: string) {
    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode, password);
      return true;
    } catch (error: any) {
      console.error('Error confirm password reset', error);
      await this.utils.showToast({ message: this.translate.instant('auth.error.recovery_password_link_error'), cssClass: 'warning' })
      return false;
    }
  }

  async loginWithEmailAndPassword(email: string, password: string) {
    try {
      const auth = getAuth();
      const result = await signInWithEmailAndPassword(auth, email, password)
      await this.rest.getCurrentUser();
      const userId = result?.user?.uid;
      if (userId) {
        const resp = await this.rest.getCurrentUser();
        if (resp) {
          if (resp.role?.includes('admin')) {
            await this.nav.navigateRoot('');
          } else if (resp.role?.includes('manager')) {
            await this.nav.navigateRoot('');
            // Проверяем контракты провинции для менеджера
            await this.checkProvinceContracts(resp);
          } else if (resp.role?.includes('operator')) {
            await this.nav.navigateRoot('sip');
          }
        }

        return userId;
      } else {
        return false;
      }
    } catch (error: any) {
      console.error('error loginWithEmailAndPassword', error);
      await this.utils.showToast({
        header: '¡Error!', message: await (this.translate.instant('auth.error.firebase.' + error.code)), cssClass: 'danger'
      });
      return false;
    }
  }

  async checkProvinceContracts(user: any) {
    console.log('🌎 checkProvinceContracts', user);
    console.log('🌎 provinceContractStatus', user?.provinceContractStatus);

    // Проверяем наличие поля provinceContractStatus и что hasSignedContract === false
    if (!user?.provinceContractStatus) {
      console.log('❌ No provinceContractStatus in user object');
      return;
    }

    if (user.provinceContractStatus.hasSignedContract) {
      console.log('✅ All contracts already signed');
      return;
    }

    const provinceId = user.provinceContractStatus.provinceId;
    console.log('🌎 Province ID:', provinceId);

    if (!provinceId) {
      console.log('❌ No provinceId in provinceContractStatus');
      return;
    }

    try {
      // Получаем список контрактов для подписания
      console.log('📋 Getting pending contracts for province:', provinceId);
      const contractsResp = await this.rest.getProvincePendingContracts(provinceId);
      console.log('📋 Contracts response:', contractsResp);

      if (contractsResp.ok && contractsResp.response && contractsResp.response.length > 0) {
        console.log('✅ Found contracts to sign:', contractsResp.response.length);

        // Показываем модальное окно для подписания контрактов
        // Модальное окно можно закрыть только если все контракты подписаны
        const modal = await this.modalController.create({
          component: ContractsModalComponent,
          componentProps: {
            config: {
              title: this.translate.instant('province_contracts.title'),
              contracts: contractsResp.response,
              provinceId: provinceId
            }
          },
          backdropDismiss: false,
          canDismiss: async (data) => {
            // Разрешаем закрытие только если все контракты подписаны
            return data?.allSigned === true;
          },
          cssClass: 'contracts-modal'
        });

        console.log('🎯 Presenting modal...');
        await modal.present();

        // Ждем закрытия модального окна
        const result = await modal.onWillDismiss();
        console.log('🎯 Modal dismissed with result:', result);

        // Если все контракты подписаны, обновляем данные пользователя
        if (result.data?.allSigned) {
          console.log('✅ All contracts signed, refreshing user data');
          await this.rest.getCurrentUser(true);
        }
      } else {
        console.log('❌ No contracts to sign or error:', contractsResp);
        if (!contractsResp.ok) {
          console.error('Error getting pending contracts:', contractsResp);
        }
        if (!contractsResp.response || contractsResp.response.length === 0) {
          console.log('⚠️ No contracts returned from API');
        }
      }
    } catch (error) {
      console.error('❌ Error checking province contracts:', error);
    }
  }

  async logout() {
    try {
      console.log('logout user');
      const auth = getAuth();
      await signOut(auth);
      await this.storage.deleteItem('taxi_backoffice_current_user');

      this.analytics.sendEventByType('logout');
    } catch (error: any) {
      console.log(error);
      this.utils.showToast({ message: await this.translate.instant(`errorMessages.firebase.${error.code}`), cssClass: 'danger' });
    }
  }

}
